import styled from "styled-components";
import { media } from "../../styles/breakpoints";

const StructuresStyled = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.md};

  ${media.tablet`
    padding: 24px;
  `}

  ${media.desktop`
    padding: 32px;
  `}

  ${media.wide`
    padding: 40px;
  `}

  ${media.ultraWide`
    padding: 48px;
  `}

  .structures {
    &__title {
      color: ${(props) => props.theme.colour.brand.primary};
      font-size: ${(props) => props.theme.fonts.titleFontSizeL};
      font-weight: ${(props) => props.theme.fonts.fontWeightBold};
      margin-bottom: ${(props) => props.theme.spacing.lg};
      text-align: center;
      line-height: ${(props) => props.theme.fonts.lineHeightTight};

      ${media.tablet`
        font-size: 2.5rem;
        margin-bottom: 32px;
      `}

      ${media.desktop`
        font-size: 3rem;
        margin-bottom: 48px;
      `}

      ${media.wide`
        font-size: 3.5rem;
        margin-bottom: 64px;
      `}
    }

    &__header {
      margin-bottom: ${(props) => props.theme.spacing.lg};
      padding: ${(props) => props.theme.spacing.md};
      background-color: ${(props) => props.theme.colour.background.secondary};
      border-radius: ${(props) => props.theme.border.borderRadiusXL};
      border: 1px solid ${(props) => props.theme.colour.border.light};

      ${media.tablet`
        margin-bottom: 32px;
        padding: 20px;
      `}

      ${media.desktop`
        margin-bottom: 40px;
        padding: 24px;
      `}

      ${media.wide`
        margin-bottom: 48px;
        padding: 28px;
      `}
    }

    &__controls {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;

      @media (max-width: 480px) {
        flex-direction: column;
        gap: ${(props) => props.theme.spacing.md};
      }

      ${media.tablet`
        gap: 20px;
        flex-wrap: nowrap;
      `}

      ${media.desktop`
        gap: 24px;
      `}

      ${media.wide`
        gap: 32px;
      `}
    }

    &__counter {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: ${(props) => props.theme.spacing.sm}
        ${(props) => props.theme.spacing.md};
      background-color: ${(props) => props.theme.colour.interactive.primary};
      color: ${(props) => props.theme.colour.text.inverse};
      border-radius: ${(props) => props.theme.border.borderRadiusLarge};
      font-size: ${(props) => props.theme.fonts.textFontSizeM};
      font-weight: ${(props) => props.theme.fonts.fontWeightMedium};
      min-width: 80px;
      min-height: 40px;
      flex-shrink: 0;

      ${media.tablet`
        padding: 12px 20px;
        font-size: 1.125rem;
        min-width: 100px;
        min-height: 44px;
      `}

      ${media.desktop`
        padding: 14px 24px;
        font-size: 1.25rem;
        min-width: 120px;
        min-height: 48px;
      `}

      ${media.wide`
        padding: 16px 28px;
        font-size: 1.375rem;
        min-width: 140px;
        min-height: 52px;
      `}
    }

    &__filters {
      flex: 1;
      display: flex;
      justify-content: center;
      min-width: 150px;
      max-width: 400px;

      @media (max-width: 480px) {
        min-width: 200px;
        max-width: none;
      }

      ${media.tablet`
        min-width: 200px;
        max-width: 350px;
      `}

      ${media.desktop`
        min-width: 250px;
        max-width: 450px;
      `}

      ${media.wide`
        min-width: 300px;
        max-width: 500px;
      `}
    }

    &__location-toggle {
      display: flex;
      justify-content: center;
      flex-shrink: 0;

      .location-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        background-color: ${(props) => props.theme.colour.background.primary};
        border: 2px solid ${(props) => props.theme.colour.border.medium};
        border-radius: 50%;
        cursor: pointer;
        transition: ${(props) => props.theme.transitions.normal};
        color: ${(props) => props.theme.colour.text.primary};

        ${media.tablet`
          width: 48px;
          height: 48px;
        `}

        ${media.desktop`
          width: 52px;
          height: 52px;
        `}

        ${media.wide`
          width: 56px;
          height: 56px;
        `}

        &:hover {
          background-color: ${(props) =>
            props.theme.colour.interactive.primary};
          color: ${(props) => props.theme.colour.text.inverse};
          border-color: ${(props) => props.theme.colour.interactive.primary};
          transform: translateY(-2px);
          box-shadow: ${(props) => props.theme.shadows.medium};
        }

        &:focus-visible {
          outline: 2px solid ${(props) => props.theme.colour.border.focus};
          outline-offset: 2px;
        }

        svg {
          font-size: 1.25rem;

          ${media.tablet`
            font-size: 1.5rem;
          `}

          ${media.desktop`
            font-size: 1.75rem;
          `}

          ${media.wide`
            font-size: 2rem;
          `}
        }
      }
    }

    &__info {
      display: flex;
      flex-wrap: wrap;
      gap: ${(props) => props.theme.spacing.sm};
      margin: ${(props) => props.theme.spacing.lg} 0;
      justify-content: center;

      ${media.tablet`
        gap: 16px;
        margin: 32px 0;
      `}

      ${media.desktop`
        gap: 20px;
        margin: 48px 0;
      `}

      ${media.wide`
        gap: 24px;
        margin: 64px 0;
      `}

      .info-item {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: ${(props) => props.theme.spacing.sm}
          ${(props) => props.theme.spacing.md};
        background-color: ${(props) => props.theme.colour.background.secondary};
        border: 1px solid ${(props) => props.theme.colour.border.light};
        border-radius: ${(props) => props.theme.border.borderRadiusLarge};
        font-size: ${(props) => props.theme.fonts.textFontSizeS};
        font-weight: ${(props) => props.theme.fonts.fontWeightMedium};
        color: ${(props) => props.theme.colour.text.primary};
        transition: ${(props) => props.theme.transitions.normal};
        min-height: 44px;

        ${media.tablet`
          padding: 16px 24px;
          font-size: 1.25rem;
        `}

        ${media.desktop`
          padding: 20px 32px;
          font-size: 1.5rem;
        `}

        ${media.wide`
          padding: 24px 40px;
          font-size: 1.75rem;
        `}

        &:hover {
          background-color: ${(props) =>
            props.theme.colour.interactive.primary};
          color: ${(props) => props.theme.colour.text.inverse};
          transform: translateY(-1px);
          box-shadow: ${(props) => props.theme.shadows.small};
        }
      }
    }

    &__load-more {
      width: 100%;
      display: flex;
      justify-content: center;
      margin-top: ${(props) => props.theme.spacing.xl};

      ${media.desktop`
        margin-top: 48px;
      `}

      ${media.wide`
        margin-top: 64px;
      `}

      button {
        background-color: ${(props) => props.theme.colour.interactive.primary};
        color: ${(props) => props.theme.colour.text.inverse};
        font-size: ${(props) => props.theme.fonts.textFontSizeM};
        font-weight: ${(props) => props.theme.fonts.fontWeightMedium};
        padding: ${(props) => props.theme.spacing.md}
          ${(props) => props.theme.spacing.xl};
        border: none;
        border-radius: ${(props) => props.theme.border.borderRadiusLarge};
        cursor: pointer;
        transition: ${(props) => props.theme.transitions.normal};
        min-height: 48px;
        min-width: 120px;

        ${media.tablet`
          font-size: 1.5rem;
          padding: 24px 48px;
          min-width: 160px;
        `}

        ${media.desktop`
          font-size: 1.75rem;
          padding: 28px 56px;
          min-width: 180px;
        `}

        ${media.wide`
          font-size: 2rem;
          padding: 32px 64px;
          min-width: 200px;
        `}

        &:hover:not(:disabled) {
          background-color: ${(props) => props.theme.colour.interactive.hover};
          transform: translateY(-2px);
          box-shadow: ${(props) => props.theme.shadows.medium};
        }

        &:focus-visible {
          outline: 2px solid ${(props) => props.theme.colour.border.focus};
          outline-offset: 2px;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
      }
    }

    &__notFound {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: ${(props) => props.theme.spacing.xxl}
        ${(props) => props.theme.spacing.lg};
      text-align: center;
      min-height: 300px;

      ${media.tablet`
        padding: 64px 32px;
        min-height: 400px;
      `}

      ${media.desktop`
        padding: 80px 48px;
        min-height: 500px;
      `}

      ${media.wide`
        padding: 96px 64px;
        min-height: 600px;
      `}

      .not-found-icon {
        width: 64px;
        height: 64px;
        color: ${(props) => props.theme.colour.text.muted};
        margin-bottom: ${(props) => props.theme.spacing.lg};

        ${media.tablet`
          width: 80px;
          height: 80px;
          margin-bottom: 32px;
        `}

        ${media.desktop`
          width: 96px;
          height: 96px;
          margin-bottom: 40px;
        `}

        ${media.wide`
          width: 112px;
          height: 112px;
          margin-bottom: 48px;
        `}
      }

      .not-found-text {
        font-size: ${(props) => props.theme.fonts.textFontSizeL};
        color: ${(props) => props.theme.colour.text.secondary};
        margin-bottom: ${(props) => props.theme.spacing.md};

        ${media.tablet`
          font-size: 1.875rem;
          margin-bottom: 24px;
        `}

        ${media.desktop`
          font-size: 2.25rem;
          margin-bottom: 32px;
        `}

        ${media.wide`
          font-size: 2.5rem;
          margin-bottom: 40px;
        `}
      }
    }
  }

  /* Grid responsivo inteligente para las estructuras */
  .structures-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
    justify-items: center;
    list-style: none;
    padding: 0;
    margin: 0;

    ${media.tablet`
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 32px;
    `}

    ${media.desktop`
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 40px;
    `}

    ${media.wide`
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 48px;
    `}

    ${media.ultraWide`
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
      gap: 56px;
    `}
  }

  .structures-grid > li {
    display: flex;
    justify-content: center;
    padding: 0;
    width: 100%;
    margin: 0;
  }

  /* Loading skeleton */
  .loading-skeleton {
    .structure-card {
      background: ${(props) => props.theme.colour.background.secondary};
      border-radius: ${(props) => props.theme.border.borderRadiusXL};
      overflow: hidden;
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  }
`;

export default StructuresStyled;
